import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send } from "lucide-react";
import { PACKAGE_V1, PARTICIPANTS, NODES, RECEIPTS } from "./data";
import { StatusChip, Kicker, DownloadReceiptBtn, LocalBoundary } from "./ui";

const steward = NODES[0];

export const ActProposed = () => {
    const [distributed, setDistributed] = useState(false);

    return (
        <section data-testid="act-proposed">
            <Kicker>Act 1 — Change proposed</Kicker>
            <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                The Standards Steward publishes an authoritative change package
            </h2>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
                {/* Package card */}
                <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="change-package-card">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="font-mono text-sm font-medium tracking-tight text-slate-900">
                            CHANGE PACKAGE {PACKAGE_V1.id}
                        </p>
                        <StatusChip tone={distributed ? "blue" : "amber"} testId="package-status">
                            {distributed ? "DISTRIBUTED TO ROLE NODES" : "CHANGE PROPOSED"}
                        </StatusChip>
                    </div>

                    <dl className="mt-5 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                        <Field k="Current standard version" v={PACKAGE_V1.currentStandard} />
                        <Field k="Proposed standard version" v={PACKAGE_V1.proposedStandard} />
                        <Field k="New field" v={<span className="font-mono">{PACKAGE_V1.field}</span>} />
                        <Field
                            k="Proposed effective date"
                            v={
                                <>
                                    {PACKAGE_V1.effectiveDate}{" "}
                                    <span className="font-mono text-[10px] uppercase tracking-wide text-amber-700">
                                        {PACKAGE_V1.effectiveDateLabel}
                                    </span>
                                </>
                            }
                        />
                        <Field k="Transition window" v={PACKAGE_V1.transitionWindow} />
                        <Field k="Test cases" v={`${PACKAGE_V1.testCases} reference test cases`} />
                        <Field k="Steward signature" v={<span className="font-mono text-xs">{PACKAGE_V1.stewardSignature}</span>} />
                        <Field k="Package reference" v={<span className="font-mono text-xs">{PACKAGE_V1.packageHash}</span>} />
                    </dl>

                    <div className="mt-5 border-t border-slate-100 pt-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Definition</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{PACKAGE_V1.definition}</p>
                    </div>

                    <div className="mt-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Permitted values</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {PACKAGE_V1.permittedValues.map((v) => (
                                <span key={v} className="rounded-sm bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-700">
                                    {v}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Affected roles</p>
                            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                {PACKAGE_V1.affectedRoles.map((r) => <li key={r}>· {r}</li>)}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Affected messages</p>
                            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                                {PACKAGE_V1.affectedMessages.map((r) => <li key={r}>· {r}</li>)}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                            Reason for change — the network needs to distinguish between
                        </p>
                        <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                            {PACKAGE_V1.reason.map((r) => <li key={r}>· {r}</li>)}
                        </ul>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => setDistributed(true)}
                            disabled={distributed}
                            data-testid="distribute-btn"
                            className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                        >
                            <Send className="h-4 w-4" strokeWidth={1.5} />
                            Distribute to role nodes
                        </button>
                        <DownloadReceiptBtn
                            json={RECEIPTS[0].json}
                            filename="publication-receipt-SC-2027-014.json"
                            testId="download-publication-receipt"
                            label="Publication receipt (JSON)"
                        />
                    </div>
                </div>

                {/* Steward authority + distribution animation */}
                <div className="space-y-4">
                    <LocalBoundary testId="steward-boundary">
                        <p className="text-sm font-medium text-slate-900">{steward.name}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{steward.desc}</p>
                        <TwoCol title="May" items={steward.may} tone="text-green-700" />
                        <TwoCol title="May not" items={steward.mayNot} tone="text-red-700" />
                    </LocalBoundary>

                    <div className="rounded-sm border border-slate-200 bg-white p-4" data-testid="distribution-panel">
                        <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                            Distribution
                        </p>
                        <div className="mt-3 space-y-2">
                            {PARTICIPANTS.map((p, i) => (
                                <div key={p.id} className="flex items-center justify-between gap-2">
                                    <span className="text-sm text-slate-700">{p.name}</span>
                                    <AnimatePresence>
                                        {distributed ? (
                                            <motion.span
                                                initial={{ opacity: 0, x: -16 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.25 * i, duration: 0.35 }}
                                            >
                                                <StatusChip tone="blue" testId={`received-${p.id}`}>
                                                    Signed package received
                                                </StatusChip>
                                            </motion.span>
                                        ) : (
                                            <StatusChip tone="slate">Awaiting distribution</StatusChip>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="distribution-explainer">
                            Every participant receives the same authoritative change package.
                            Each participant evaluates it locally against its own systems,
                            evidence and authority.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

function Field({ k, v }) {
    return (
        <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{k}</dt>
            <dd className="mt-0.5 text-sm text-slate-800">{v}</dd>
        </div>
    );
}

function TwoCol({ title, items, tone }) {
    return (
        <div className="mt-3">
            <p className={`text-xs font-medium uppercase tracking-wide ${tone}`}>{title}</p>
            <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                {items.map((m) => <li key={m}>· {m}</li>)}
            </ul>
        </div>
    );
}
